/**
 * Dispatch data slice: the interface this feature's screens use + the live implementation over the US API
 * (core/http.ts → /api/v1/app/*). The fixture implementation (in-memory Lone Star tenant) is in ./fixture.ts, loaded only
 * in dev / VITE_FIXTURES builds. Shared entity reads (jobs, techs, techs/live) live in core/entities.ts.
 *
 * Paths follow P5 (api/src/modules/dispatch/routes.js: suggest, assign, settings) at `/v1` (nginx `/api/` → API root).
 * Responses are normalised here so views only see core/types shapes.
 */
import { APP, ApiError, type Http } from '@app/core/http';
import { NotAvailable, fetchJob, isMissingRoute, notAvailable } from '@app/core/live/dto';
import type { DispatchCandidate } from '@app/core/types';

export interface CandidateSet {
  items: DispatchCandidate[];
  decision_id: string | null;
  /** Techs the scorer ruled out, with why ("at_capacity", "missing_skill", …). */
  filtered: { tech_id: string; reason: string }[];
}
export interface AssignInput {
  tech_id: string;
  /** Job version the dispatcher saw (sent as If-Match; 409 stale_version when someone else got there first). */
  expected_version: number;
  decision_id?: string | null;
  reason?: string;
  reassign?: boolean;
  /** Double-book past a capacity conflict (needs jobs.override_status). */
  force?: boolean;
}
export interface AssignResult { job_id: string; tech_id: string; eta_min: number | null; noop?: boolean }
/** 409 tech_at_capacity `preview` (and P5 `slot_taken`, which may carry none). */
export interface CapacityPreview { conflicts: { job_id: string; job_no: string; scheduled_start: string | null; scheduled_end: string | null }[] }
/** 409 stale_version / already_assigned body. */
export interface StaleCurrent { version: number | null; assigned_tech_id: string | null; assigned_by_name: string | null }

export interface DispatchSettings {
  mode: 'suggest' | 'auto';
  /** Workflow engine health for auto-dispatch: 'paused' while it is down (manual assign keeps working). */
  auto_state: 'running' | 'paused';
}
export interface ServiceArea { name: string; lat: number; lng: number }

export interface Api {
  candidates(jobId: string, signal?: AbortSignal): Promise<CandidateSet>;
  assign(jobId: string, body: AssignInput, idempotencyKey: string): Promise<AssignResult>;
  settings(): Promise<DispatchSettings>;
  setMode(mode: DispatchSettings['mode']): Promise<DispatchSettings>;
  serviceArea(): Promise<ServiceArea[]>;
  /** P4 bridge: rings the dispatcher's verified phone, then the tech, from the business number. */
  callTech(techId: string, jobId: string | null, idempotencyKey: string): Promise<{ call_id: string }>;
  markBreak(techId: string, idempotencyKey: string): Promise<{ tech_id: string; state: 'break' }>;
}

const num = (v: unknown) => (v == null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));

/** Scorer rows arrive in the site shape (camelCase) from P5's suggest, or snake_case; accept both. */
export function normCandidate(c: any): DispatchCandidate | null {
  const tech_id = c?.tech_id ?? c?.techId;
  if (typeof tech_id !== 'string') return null;
  return {
    tech_id, score: Math.round(Number(c.score) || 0),
    eta_min: num(c.eta_min ?? c.etaMin) ?? 0, distance_mi: num(c.distance_mi ?? c.distanceMi) ?? 0,
    reasons: Array.isArray(c.reasons) ? c.reasons.filter((r: unknown) => typeof r === 'string') : [],
  };
}
const normSet = (r: any): CandidateSet => ({
  items: (Array.isArray(r?.candidates) ? r.candidates : Array.isArray(r?.items) ? r.items : []).map(normCandidate).filter(Boolean)
    .sort((a: DispatchCandidate, b: DispatchCandidate) => b.score - a.score),
  decision_id: r?.decision_id ?? null,
  filtered: (Array.isArray(r?.filtered) ? r.filtered : []).map((f: any) => ({ tech_id: f.tech_id ?? f.techId, reason: f.reason ?? f.filtered_reason ?? '' }))
    .filter((f: { tech_id: unknown }) => typeof f.tech_id === 'string'),
});
const normSettings = (r: any): DispatchSettings => {
  const s = r?.settings ?? r ?? {};
  return { mode: s.mode === 'auto' ? 'auto' : 'suggest', auto_state: s.auto_state === 'paused' || s.workflows === 'down' ? 'paused' : 'running' };
};

const enc = encodeURIComponent;
/** P5 dispatch is mounted at the API root (`/v1/dispatch/*`, `/v1/jobs/:id/*`), not under `/v1/app`. */
const P5 = '/v1';
/** Service-area outlines have no P5 route yet: a missing route reads as none. */
const orDefault = <T>(p: Promise<T>, d: T) => p.catch((e) => { if (isMissingRoute(e)) return d; throw e; });

/**
 * Live. Suggest / assign / settings = P5 (api/src/modules/dispatch/routes.js): POST /v1/jobs/:id/dispatch/suggest,
 * POST /v1/jobs/:id/assign {tech_id, decision_id?, override_reason?, reassign?} (records the AI decision, flips MECHANIC
 * ALLOCATED; 409 already_assigned / tech_double_booked, 400 override_reason_required), GET/PATCH /v1/dispatch/settings.
 * Rolling out (no route yet): the P4 tech bridge (click-to-call rings customers only), tech break, service-area outlines.
 */
export const live = (http: Http): Api => ({
  candidates: (jobId, signal) => http.post<any>(`${P5}/jobs/${enc(jobId)}/dispatch/suggest`, {}, { signal }).then(normSet)
    .catch((e) => { throw isMissingRoute(e) ? new NotAvailable('dispatch_suggest') : e; }),
  assign: async (jobId, b, idempotencyKey) => {
    // P5 does not check If-Match on assign: compare the version the dispatcher saw before writing.
    const cur = await fetchJob(http, jobId);
    if (cur.version !== b.expected_version && cur.assigned_tech_id !== b.tech_id) {
      throw new ApiError(409, 'stale_version', null, { current: { version: cur.version, assigned_tech_id: cur.assigned_tech_id, assigned_by_name: null } });
    }
    if (cur.assigned_tech_id === b.tech_id) return { job_id: jobId, tech_id: b.tech_id, eta_min: null, noop: true };
    const body: Record<string, unknown> = { tech_id: b.tech_id };
    if (b.decision_id) body.decision_id = b.decision_id;
    if (b.reason?.trim()) body.override_reason = b.reason.trim().slice(0, 200);
    if (b.reassign) body.reassign = true;
    try {
      const r = await http.post<any>(`${P5}/jobs/${enc(jobId)}/assign`, body, { idempotencyKey });
      return { job_id: r?.job_id ?? jobId, tech_id: r?.tech_id ?? b.tech_id, eta_min: r?.eta_min ?? null, ...(r?.noop ? { noop: true } : {}) };
    } catch (e) {
      // P1's exclusion constraint → the board's capacity conflict (no preview rows yet; P5 has no `force`).
      if (e instanceof ApiError && (e.code === 'tech_double_booked' || e.code === 'slot_taken')) throw new ApiError(409, 'tech_at_capacity', e.requestId, { preview: { conflicts: [] } });
      throw e;
    }
  },
  settings: () => http.get<any>(`${P5}/dispatch/settings`).then(normSettings),
  setMode: (mode) => http.patch<any>(`${P5}/dispatch/settings`, { mode }).then(normSettings),
  serviceArea: () => orDefault(http.get<any>(`${APP}/dispatch/service-area`).then((r) => (Array.isArray(r) ? r : r?.items ?? [])
    .filter((a: any) => typeof a?.name === 'string' && Number.isFinite(a.lat) && Number.isFinite(a.lng))), []),
  callTech: () => notAvailable('call_tech_bridge'),
  markBreak: () => notAvailable('tech_break'),
});
